import {
  FormControl,
  InputLabel,
  Skeleton,
  Stack,
  TextField
} from '@mui/material'
import {
  RequiredFieldHelperText,
  CountryAddressSelector,
  ParentOrgSelect,
  skeletonStyles,
  OrganizationsTypeSelect, useCountriesLuts
} from 'components'
import React, { type FC } from 'react'
import { type NewOrganizationInformation } from 'services'

interface OrgBasicInfoFieldsProps {
  loading: boolean
  disabled: boolean
  value: NewOrganizationInformation
  updateValue: (
    updater: (prevState: any) => any
  ) => void
  isFieldEmpty: (key: string) => boolean
}

export const OrgBasicInfoFields: FC<OrgBasicInfoFieldsProps> = ({
  loading,
  disabled,
  value,
  updateValue,
  isFieldEmpty
}) => {
  const { lutValues: countriesLutValues } = useCountriesLuts()

  const updateField = (field: string, newValue: any): void => {
    updateValue((prevState) => {
      if (isPropPresentAndDifferent(field, newValue, prevState)) {
        const updatedState = { ...prevState, [field]: newValue }
        if (field === 'country') {
          updatedState.parentOrganization = null
        }
        return updatedState
      }
      return prevState
    })
  }

  const isPropPresentAndDifferent = (
    field: string,
    newValue: any,
    prevState: NewOrganizationInformation
  ): boolean => {
    return (
      field in prevState &&
      prevState[field as keyof NewOrganizationInformation] !== newValue
    )
  }

  return (
    <Stack spacing={1}>
      {loading
        ? (
          <>
            {Array.from({ length: 5 }).map((_, index) => (
              <Skeleton key={index} variant="rectangular" sx={skeletonStyles} />
            ))}
          </>
          )
        : (
          <>
            <TextField
              id="orgName"
              disabled={disabled}
              label="Name"
              variant="outlined"
              value={value.organizationName}
              onChange={(e) => { updateField('organizationName', e.target.value) } }
              fullWidth />
            <RequiredFieldHelperText isEmpty={isFieldEmpty('organizationName')}>
              This field is required
            </RequiredFieldHelperText>

            <TextField
              id="orgLocalName"
              disabled={disabled}
              label="Local Name"
              variant="outlined"
              value={value.organizationNameLocal}
              onChange={(e) => { updateField('organizationNameLocal', e.target.value) } }
              fullWidth />
            <RequiredFieldHelperText isEmpty={isFieldEmpty('organizationNameLocal')}>
              This field is required
            </RequiredFieldHelperText>

            <CountryAddressSelector
              countries={countriesLutValues.countries}
              value={value.country}
              setValue={(e) => { updateField('country', e) } }
              disabled={disabled}
            />

            <FormControl variant="outlined" fullWidth={true}>
              <InputLabel id="type-select-label">Type</InputLabel>
              <OrganizationsTypeSelect
                  filters={value}
                  disabled={disabled}
                  updateFilters={(filters) => { updateField('orgTypeId', filters.type) } } />
            </FormControl>

            <RequiredFieldHelperText isEmpty={isFieldEmpty('orgTypeId')}>
              This field is required
            </RequiredFieldHelperText>
            <ParentOrgSelect
              filters={value}
              allOrgs={false}
              disabled={disabled}
              onChange={(e) => { updateField('parentOrganization', e[0]) } } />
          </>
          )}
    </Stack>
  )
}

export default OrgBasicInfoFields
